import { Icon } from '@iconify/react'
import { observe } from '@legendapp/state'
import { useObservable } from '@legendapp/state/react'
import { Computed } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { addFeatures } from '~features/features/api'
// import { fetchProductFeatureList } from '~features/product-features/api'
import { fetchProductPrivilegeList } from '~features/product-privileges/api'
// import { TemplateProductFeatureSetting } from '~features/templates/types'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
  Label,
  Textarea,
} from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import {
  Option,
} from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchProductList } from '~in-app/shareable/products/api'
import { Product } from '~in-app/types'
import { featuresValidation } from '~in-app/validation/features.validation'

export const Route = createFileRoute('/console/products/features/add')({
  component: AddFeaturePage,
  staticData: {
    privileges: ['0.3.8.5'],
  },
})

function AddFeaturePage() {
  const router = useRouter()
  const state$ = useObservable({
    productIds: '',
    type: '',
  })
  // const [activeStep, setActiveStep] = useState(0)
  const steps = ['Feature Details', 'Product Feature Settings']
  const formOpts = formOptions({
    defaultValues: {
      product: [] as Product[],
      productId: '',
      defaultValue: '',
      remarks: '',
      desp: '',
      version: 0,
      code: '',
      name: '',
      active: true,
      priviledgeIds: [],
      priviledges: '',
      type: '',
      current: false,
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: featuresValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitNewFeatures(value)
      }
      catch (error) {
        console.error('Add Features failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })
  const formControl = useForm(formOpts)
  const typeOptions = [
    { label: 'Value', value: 'V' },
    { label: 'Privilege', value: 'P' },
    { label: 'Boolean', value: 'B' },
  ]

  const { data: productList = [] } = useQuery<Option[]>({
    queryKey: ['add-features-products-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const response = await fetchProductList({
        abortCtrl: signal,
      })
      const productList = (response?.list ?? []).map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      return productList
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  function buildPrivilegeTree(privileges: any[]) {
    const privilegeMap = new Map()

    // Step 1: Initialize all privileges in a map
    privileges.forEach((privilege) => {
      if (privilege.value.length === 3) {
        setCollapsedIds(prev => [...prev, privilege.id])
      }

      const isSpecialValue = ['0', '0.1', '0.3', '0.3.7', '0.3.7.0', '0.3.7.1'].includes(privilege.value)
      privilegeMap.set(privilege.id, {
        ...privilege,
        children: [],
        isExpanded: true,
        isChecked: true,
        isDisable: isSpecialValue,
      })
    })

    // Step 2: Link children to their parents
    privileges.forEach((privilege) => {
      if (privilege.parent) {
        const parentPrivilege = privilegeMap.get(privilege.parent)
        if (parentPrivilege) {
          parentPrivilege.children.push(privilegeMap.get(privilege.id))
          // Sort children by value in ascending order
          parentPrivilege.children.sort((a: { value: string }, b: { value: any }) => a.value.localeCompare(b.value, undefined, { numeric: true }))
        }
      }
    })
    // selectedFeatureValues.includes(privilege.value)
    const filteredTreeData = Array.from(privilegeMap.values()).filter(
      (privilege: any) => !privilege.parent,
    )
    // Step 3: Return the root-level privileges
    return filteredTreeData.map((privilege: any) => {
      // const matchedFeature = featureList.find(
      //   (feature: any) => feature.featureValue === privilege.value,
      // )
      // if (matchedFeature) {
      //   return {
      //     ...matchedFeature,
      //     isChecked: true,
      //     fixed: privilege.value === '0' ? true : undefined,
      //     isDisable: true,
      //     parent: privilege.parent,
      //     children: privilege.children,
      //     isExpanded: privilege.isExpanded,
      //   }
      // }
      return privilege
    })
  }
  const { data: productPrivilegeList = [], refetch } = useQuery<Option[]>({
    queryKey: ['add-features-product-privileges-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (state$.productIds.get() === '') {
        return [] as Option[]
      }
      const fetchAllPrivileges = async (
        page = 0,
        accumulatedPrivileges: any[] = [],
      ): Promise<any[]> => {
        const response = await fetchProductPrivilegeList({
          query: { productIds: state$.productIds.get(), page, size: 100 },
        })
        if (response) {
          const newPrivileges = accumulatedPrivileges.concat(response.list)
          if (page + 1 < response.totalPage) {
            return fetchAllPrivileges(page + 1, newPrivileges)
          }
          return newPrivileges
        }
        return accumulatedPrivileges
      }

      const allPrivileges = await fetchAllPrivileges()
      const privilegeList = allPrivileges.map((item: any) => ({
        label: item.name,
        value: item.code,
        parent: item?.parent?.id,
        id: item.id,
      }))
      const productResponse = buildPrivilegeTree(privilegeList)
      // formControl.setFieldValue('privilegeFeatures', [
      //   {
      //     ...productResponse.find(
      //       (feature: any) => feature.featureValue === '0',
      //     ),
      //   },
      // ])
      return productResponse
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  useEffect(() => {
    const disposer = observe(
      state$,
      (e) => {
        if (e.previous !== undefined && e.value !== undefined) {
          refetch()
        }
      },
      { immediate: false },
    )

    return disposer
  }, [])

  // const { data: featureList = [] } = useQuery<any[]>({
  //   // Add explicit type here
  //   queryKey: ['add-features-features-listing'],
  //   queryFn: async () => {
  //     const response = await fetchProductFeatureList()
  //     let counter1 = 0
  //     const reponseFeatureList = (response?.list ?? [])
  //       .filter((val: any) => val.priviledges !== undefined)
  //       .map((item: any) => ({
  //         featureValue: item?.priviledges[0]?.id,
  //         featureName: item.name,
  //         featureCode: item.code,
  //         featureId: item.id,
  //         privileges: item.priviledges,
  //         label: item.name,
  //         value: item.id,
  //       }))

  //     const productFeatureSettingsResponse = (response?.list ?? [])
  //       .filter((val: any) => val.priviledges === undefined)
  //       .map((item: any) => ({
  //         featureValue: item.defaultValue,
  //         featureName: item.name,
  //         featureCode: item.code,
  //         featureId: item.id,
  //         privileges: item.priviledges,
  //       }))
  //     // Set the form value of productFeatureSettings
  //     // formControl.setFieldValue(
  //     //   'featureSettings',
  //     //   productFeatureSettingsResponse,
  //     // )

  //     return reponseFeatureList
  //   },
  //   refetchOnMount: true,
  //   refetchOnWindowFocus: false,
  //   refetchOnReconnect: false,
  // })

  const addFeaturesFunc = () => {
    const mutation = useMutation({
      mutationFn: addFeatures,
      onSuccess: (response) => {
        toast.success(`Features Created Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/products/features/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const addFeaturesMutation = addFeaturesFunc()
  const submitNewFeatures = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))

      const collectCheckedPrivileges = (privileges: any[]): string[] => {
        let checkedPrivileges: string[] = []
        privileges.forEach((privilege: any) => {
          if (privilege.isChecked) {
            if (privilege.featureValue) {
              checkedPrivileges.push(privilege.featureValue)
            }
            else {
              checkedPrivileges.push(privilege.value)
            }
          }
          if (privilege.children) {
            checkedPrivileges = checkedPrivileges.concat(
              collectCheckedPrivileges(privilege.children),
            )
          }
        })
        return checkedPrivileges
      }
      const firstPrivilegeId = collectCheckedPrivileges(
        submitValue.priviledgeIds,
      )[0]
      submitValue.priviledgeIds = [firstPrivilegeId]
      // if no need only module can change to below method
      // submitValue.priviledgeIds = collectCheckedPrivileges(
      //   submitValue.priviledgeIds,
      // )
      if (submitValue.type === 'P') {
        submitValue.defaultValue = submitValue.priviledges
      }
      if (submitValue.type === 'V' || submitValue.type === 'B') {
        delete submitValue.priviledgeIds
      }
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '' || key === 'product') {
          delete submitValue[key]
        }
      })
      addFeaturesMutation.mutate(submitValue)
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  // const handleNext = async (e: React.MouseEvent) => {
  //   e.preventDefault() // Prevent form submission
  //   if (activeStep === steps.length - 1) {
  //     formControl.handleSubmit()
  //   }
  //   else {
  //     // Validate current step before proceeding
  //     const fieldMeta = formControl.state.fieldMeta
  //     if (fieldMeta) {
  //       const fieldsToValidate = Object.keys(fieldMeta) as string[]
  //       const validationResults = await Promise.all(
  //         fieldsToValidate.map(async (field) => {
  //           const result = await formControl.validateField(
  //             field as any,
  //             'change',
  //           )
  //           return { field, result }
  //         }),
  //       )
  //       const isValid = validationResults.every(
  //         ({ result }) => Array.isArray(result) && result.length === 0,
  //       )
  //       if (isValid) {
  //         setActiveStep(prevActiveStep => prevActiveStep + 1)
  //       }
  //       else {
  //         for (const { field, result: errors } of validationResults) {
  //           if (errors.length > 0) {
  //             const fieldElement = document.getElementById(field)
  //             fieldElement?.focus()
  //             // Scroll to the field element if it exists
  //             fieldElement?.scrollIntoView({
  //               behavior: 'smooth',
  //               block: 'center',
  //             })
  //             break
  //           }
  //         }
  //       }
  //     }
  //   }
  // }

  // const handleBack = () => {
  //   setActiveStep(prevActiveStep => prevActiveStep - 1)
  // }

  const [collapsedIds, setCollapsedIds] = useState<string[]>([])

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">Add New Features</span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            onSubmit={
              (e) => {
                e.preventDefault()
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              <h4 className="mt-2 text-xl font-semibold">
                Feature Details
              </h4>

              <div
                className="grid grid-cols-2 gap-2"
              >
                <div className="grid gap-2">
                  <formControl.Field name="name">
                    {
                      field => (
                        <>
                          <div className="relative">
                            <Label
                              required
                              className="text-xs"
                              htmlFor="templateName"
                            >
                              Name
                            </Label>

                            <div className="relative flex w-full flex-row items-center self-center">
                              <Input
                                id="name"
                                type="text"
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={
                                  (e) => {
                                    field.handleChange(e.target.value),
                                    field.validate('change')
                                  }
                                }
                              />
                            </div>
                          </div>

                          {
                            field.state.meta.errors && (
                              <em
                                className="error-message"
                                role="alert"
                              >
                                {field.state.meta.errors.join(', ')}
                              </em>
                            )
                          }
                        </>
                      )
                    }
                  </formControl.Field>
                </div>

                <div className="grid gap-2">
                  <formControl.Field name="code">
                    {
                      field => (
                        <>
                          <div className="relative">
                            <Label
                              required
                              className="text-xs"
                              htmlFor="code"
                            >
                              Code
                            </Label>

                            <div className="relative flex w-full flex-row items-center self-center">
                              <Input
                                id="code"
                                type="text"
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={
                                  (e) => {
                                    const newValue = e.target.value.replace(/\s/g, '_')
                                    field.handleChange(newValue),
                                    field.validate('change')
                                  }
                                }
                              />
                            </div>
                          </div>

                          {
                            field.state.meta.errors && (
                              <em
                                className="error-message"
                                role="alert"
                              >
                                {field.state.meta.errors.join(', ')}
                              </em>
                            )
                          }
                        </>
                      )
                    }
                  </formControl.Field>
                </div>

              </div>

              <div className="grid grid-cols-3 gap-3">
                {/* Products Field */}
                <div className="flex flex-col gap-1">
                  <formControl.Field name="productId">
                    {
                      field => (
                        <>
                          <Label
                            required
                            className="text-xs"
                            htmlFor="productId"
                          >
                            Products
                          </Label>

                          <div className="min-h-16">
                            <Computed>
                              {
                                () => (
                                  <Dropdown
                                    id="productId"
                                    label=""
                                    options={productList}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        if (e !== '') {
                                          field.handleChange(e)
                                          field.validate('change')
                                          state$.productIds.set(e)
                                        }
                                      }
                                    }
                                  />
                                )
                              }
                            </Computed>

                            {
                              field.state.meta.errors && (
                                <em
                                  className="error-message text-xs text-red-500"
                                  role="alert"
                                >
                                  {field.state.meta.errors.join(', ')}
                                </em>
                              )
                            }
                          </div>
                        </>
                      )
                    }
                  </formControl.Field>
                </div>

                {/* Type Field */}
                <div className="flex flex-col gap-1">
                  <formControl.Field name="type">
                    {
                      field => (
                        <>
                          <Label
                            required
                            className="text-xs"
                            htmlFor="type"
                          >
                            Type
                          </Label>

                          <div className="min-h-16">
                            <Computed>
                              {
                                () => (
                                  <Dropdown
                                    id="type"
                                    label=""
                                    options={typeOptions}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        field.handleChange(e)
                                        state$.type.set(e)
                                        field.validate('change')
                                        formControl.setFieldValue('defaultValue', '')
                                        if (e === 'V' || e === 'B') {
                                          formControl.setFieldValue('priviledges', '')
                                          formControl.setFieldValue('priviledgeIds', [])
                                        }
                                      }
                                    }
                                  />
                                )
                              }
                            </Computed>

                            {
                              field.state.meta.errors && (
                                <em
                                  className="error-message text-xs text-red-500"
                                  role="alert"
                                >
                                  {field.state.meta.errors.join(', ')}
                                </em>
                              )
                            }
                          </div>
                        </>
                      )
                    }
                  </formControl.Field>
                </div>

                {/* Default Value Field */}
                <div className="flex flex-col gap-1 self-center">
                  <formControl.Field name="defaultValue">
                    {
                      field => (
                        <>
                          <Computed>
                            {
                              () => {
                                const fieldType = formControl.getFieldValue('type')
                                return fieldType === 'B'
                                  ? (
                                      <div className="flex items-center gap-2">
                                        <Label
                                          required
                                          className="text-xs"
                                          htmlFor="defaultValue"
                                        >
                                          Default Value
                                        </Label>

                                        <input
                                          checked={field.state.value === '1'}
                                          id="defaultValue"
                                          type="checkbox"
                                          onBlur={field.handleBlur}
                                          onChange={
                                            (e) => {
                                              const newValue = e.target.checked ? '1' : '0'
                                              field.handleChange(newValue)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      </div>
                                    )
                                  : (
                                      <div className="flex flex-col gap-1">
                                        <Label
                                          required
                                          className="text-xs"
                                          htmlFor="defaultValue"
                                        >
                                          Default Value
                                        </Label>

                                        <div className="min-h-16">
                                          <Input
                                            disabled={fieldType === 'P' || fieldType === ''}
                                            id="defaultValue"
                                            type="number"
                                            value={
                                              field.state.value === '' || field.state.value === '0'
                                                ? field.state.value
                                                : field.state.value.replace(/^0+/, '')
                                            }
                                            onBlur={field.handleBlur}
                                            onChange={
                                              (e) => {
                                                field.handleChange(e.target.value)
                                                field.validate('change')
                                              }
                                            }
                                          />

                                          {
                                            field.state.meta.errors && (
                                              <em
                                                className="error-message text-xs text-red-500"
                                                role="alert"
                                              >
                                                {field.state.meta.errors.join(', ')}
                                              </em>
                                            )
                                          }
                                        </div>
                                      </div>
                                    )
                              }
                            }
                          </Computed>
                        </>
                      )
                    }
                  </formControl.Field>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1">
                {/* Description Field */}
                <div className="mb-2 grid">
                  <formControl.Field name="desp">
                    {
                      field => (
                        <>
                          <div className="relative">
                            <Label
                              className="text-xs"
                              htmlFor="templateDescriptions"
                            >
                              Descriptions
                            </Label>

                            <Input
                              id="templateDescriptions"
                              placeholder="Type your Descriptions here."
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={
                                e =>
                                  field.handleChange(e.target.value)
                              }
                            />
                          </div>

                          {
                            field.state.meta.errors && (
                              <em
                                className="error-message"
                                role="alert"
                              >
                                {field.state.meta.errors.join(', ')}
                              </em>
                            )
                          }
                        </>
                      )
                    }
                  </formControl.Field>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1">
                {/* Description Field */}

                {/* Remarks Field */}
                <div className="mb-2 grid">
                  <formControl.Field name="remarks">
                    {
                      field => (
                        <>
                          <div className="relative">
                            <Label
                              className="text-xs"
                              htmlFor="templateRemarks"
                            >
                              Remarks
                            </Label>

                            <Textarea
                              id="remarks"
                              placeholder="Type your Remarks here."
                              rows={2}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={
                                e =>
                                  field.handleChange(e.target.value)
                              }
                            />
                          </div>

                          {
                            field.state.meta.errors && (
                              <em
                                className="error-message"
                                role="alert"
                              >
                                {field.state.meta.errors.join(', ')}
                              </em>
                            )
                          }
                        </>
                      )
                    }
                  </formControl.Field>
                </div>
              </div>

              <Computed>
                {
                  () => (
                    state$.type.get() === 'P' && (
                      <>
                        <h4 className="mt-2 text-lg font-semibold">
                          Product Privilege Settings
                        </h4>

                        <div className="mt-4 grid grid-cols-1 gap-1">
                          <div className="grid gap-2">
                            <formControl.Field name="priviledges">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        className="text-xs"
                                        htmlFor="priviledges"
                                      >
                                        Privileges
                                      </Label>

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              isClearable
                                              id="priviledges"
                                              label=""
                                              options={productPrivilegeList}
                                              value={field.state.value as any}
                                              onValueChange={
                                                (e: any) => {
                                                  field.handleChange(e)
                                                  field.validate('change')
                                                  formControl.setFieldValue('priviledgeIds', productPrivilegeList.filter(item => item.value === e) as any)
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>

                          {/* <div className="grid gap-2">
                            <formControl.Field name="priviledgeIds">
                              {
                                field => (
                                  <>
                                    <div className="mt-1">
                                      <Computed>
                                        {
                                          () => {
                                            const renderPrivilegeItem = (
                                              privilege: any,
                                              level: number = 0,
                                            ) => {
                                              const handleToggleCollapse = (
                                                privilegeValue: string,
                                              ) => {
                                                setCollapsedIds((prevCollapsedIds) => {
                                                  if (
                                                    prevCollapsedIds.includes(
                                                      privilegeValue,
                                                    )
                                                  ) {
                                                    return prevCollapsedIds.filter(
                                                      id => id !== privilegeValue,
                                                    )
                                                  }
                                                  else {
                                                    return [
                                                      ...prevCollapsedIds,
                                                      privilegeValue,
                                                    ]
                                                  }
                                                })
                                              }

                                              const updateChildren = (
                                                children: any[],
                                                isChecked: boolean,
                                              ): any[] => {
                                                return children.map(child => ({
                                                  ...child,
                                                  isChecked,
                                                  children: updateChildren(
                                                    child.children,
                                                    isChecked,
                                                  ), // Recursively update children
                                                }))
                                              }

                                              const updateParents = (
                                                privileges: any[],
                                                privilegeValue: string,
                                                isChecked: boolean,
                                              ): any[] => {
                                                const updatePrivilege = (
                                                  privileges: any[],
                                                  privilegeValue: string,
                                                  isChecked: boolean,
                                                ): boolean => {
                                                  let hasCheckedChildren = false

                                                  for (const item of privileges) {
                                                    if (item.value === privilegeValue) {
                                                      // Update the target privilege
                                                      item.isChecked = isChecked
                                                    }

                                                    if (
                                                      item.children
                                                      && item.children.length > 0
                                                    ) {
                                                      // Recursively update children
                                                      const childHasChecked
                                                        = updatePrivilege(
                                                          item.children,
                                                          privilegeValue,
                                                          isChecked,
                                                        )
                                                      hasCheckedChildren
                                                        = hasCheckedChildren
                                                        || childHasChecked
                                                    }

                                                    // After processing children, determine parent's `isChecked` based on children's state
                                                    if (
                                                      item.children
                                                      && item.children.length > 0
                                                    ) {
                                                      const anyChildChecked
                                                        = item.children.some(
                                                          (child: any) => child.isChecked,
                                                        )
                                                      item.isChecked
                                                        = anyChildChecked || item.isChecked
                                                    }
                                                  }

                                                  // Check if any item in this level is checked
                                                  return privileges.some(
                                                    priv => priv.isChecked,
                                                  )
                                                }

                                                const updateParentRecursively = (
                                                  privileges: any[],
                                                  privilegeValue: string,
                                                ): void => {
                                                  for (const item of privileges) {
                                                    if (
                                                      item.children
                                                      && item.children.some(
                                                        (child: any) =>
                                                          child.value === privilegeValue,
                                                      )
                                                    ) {
                                                      // Update parent based on the state of its children
                                                      const anyChildChecked
                                                        = item.children.some(
                                                          (child: any) => child.isChecked,
                                                        )
                                                      item.isChecked = anyChildChecked

                                                      // Recursively move up to update the parent's parent
                                                      updateParentRecursively(
                                                        privileges,
                                                        item.value,
                                                      )
                                                    }
                                                    else if (
                                                      item.children
                                                      && item.children.length > 0
                                                    ) {
                                                      // Search for the target recursively in children
                                                      updateParentRecursively(
                                                        item.children,
                                                        privilegeValue,
                                                      )
                                                    }
                                                  }
                                                }

                                                // Step 1: Update the target privilege and propagate changes to its children
                                                updatePrivilege(
                                                  privileges,
                                                  privilegeValue,
                                                  isChecked,
                                                )

                                                // Step 2: Recursively update all parents except the root
                                                updateParentRecursively(
                                                  privileges,
                                                  privilegeValue,
                                                )

                                                return privileges
                                              }

                                              const handleTogglePermission = (
                                                privilege: any,
                                                isChecked: boolean,
                                              ) => {
                                                const updatePrivileges = (
                                                  privileges: any[],
                                                ): any[] => {
                                                  return privileges.map((item) => {
                                                    if (item.value === privilege.value) {
                                                      // Update the current privilege and its children
                                                      return {
                                                        ...item,
                                                        isChecked,
                                                        children: updateChildren(
                                                          item.children,
                                                          isChecked,
                                                        ),
                                                      }
                                                    }

                                                    if (item.children.length > 0) {
                                                      return {
                                                        ...item,
                                                        children: updatePrivileges(
                                                          item.children,
                                                        ), // Recursively update the tree
                                                      }
                                                    }

                                                    return item
                                                  })
                                                }

                                                // Update the privileges tree
                                                let updatedPrivileges = updatePrivileges(
                                                  formControl.getFieldValue('priviledgeIds'),
                                                )

                                                // Update the parent privileges
                                                if (privilege.parent) {
                                                  updatedPrivileges = updateParents(
                                                    updatedPrivileges,
                                                    privilege.parent,
                                                    isChecked,
                                                  )
                                                }
                                                // Update the field state
                                                field.handleChange(updatedPrivileges as any)
                                              }

                                              return (
                                                <Fragment key={privilege?.value}>
                                                  <tr className="items-center">
                                                    <td
                                                      className="border-b border-r-4 border-slate-200 bg-white py-2 dark:border-gray-700 dark:bg-gray-800"
                                                      style={
                                                        {
                                                          paddingLeft: `${(level + 1) * 20}px`,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          position: 'sticky',
                                                          minWidth: '400px',
                                                          left: 0,
                                                        }
                                                      }
                                                      onClick={
                                                        (e) => {
                                                          e.preventDefault()
                                                          handleToggleCollapse(
                                                            privilege?.value,
                                                          )
                                                        }
                                                      }
                                                    >
                                                      {
                                                        privilege
                                                        && privilege?.children?.length
                                                        > 0 && (
                                                          <IconButton
                                                            className="size-4 bg-transparent"
                                                            color="muted"
                                                            style={{ marginRight: '8px' }}
                                                          >
                                                            <Icon
                                                              icon={
                                                                collapsedIds.includes(
                                                                  privilege?.id,
                                                                )
                                                                  ? 'lucide:plus'
                                                                  : 'lucide:minus'
                                                              }
                                                            />
                                                          </IconButton>
                                                        )
                                                      }

                                                      {privilege?.label}
                                                    </td>

                                                    <td
                                                      className="items-center border-b bg-white py-2 text-center dark:border-gray-700 dark:bg-gray-800"
                                                      style={{ width: '100px' }}
                                                    >
                                                      <input
                                                        checked={privilege?.isChecked}
                                                        disabled={privilege?.isDisable}
                                                        type="checkbox"
                                                        style={
                                                          {
                                                            transform: 'scale(1.5)',
                                                          }
                                                        }
                                                        onChange={
                                                          e =>
                                                            handleTogglePermission(
                                                              privilege,
                                                              e.target.checked,
                                                            )
                                                        }
                                                      />
                                                    </td>
                                                  </tr>

                                                  {
                                                    privilege?.children
                                                    && !collapsedIds.includes(
                                                      privilege?.id,
                                                    )
                                                    && privilege?.children.map(
                                                      (child: any) =>
                                                        renderPrivilegeItem(
                                                          child,
                                                          level + 1,
                                                        ),
                                                    )
                                                  }
                                                </Fragment>
                                              )
                                            }

                                            return (
                                              <div style={{ width: '100%' }}>
                                                {
                                                  Array.isArray(field.state.value)
                                                  && field.state.value.map(
                                                    (privilege: any) => (
                                                      <>
                                                        <caption
                                                          style={
                                                            {
                                                              captionSide: 'top',
                                                              textAlign: 'left',
                                                              fontWeight: 'bold',
                                                              padding: '10px',
                                                              position: 'sticky',
                                                              top: 0,
                                                              backgroundColor: 'white',
                                                              zIndex: 1,
                                                              whiteSpace: 'nowrap',
                                                            }
                                                          }
                                                        >
                                                          {privilege?.label}
                                                        </caption>

                                                        <div
                                                          key={privilege.value}
                                                          style={
                                                            {
                                                              maxHeight: '500px',
                                                              overflowY: 'auto',
                                                              border: '1px solid black',
                                                              marginBottom: '10px',
                                                            }
                                                          }
                                                        >
                                                          <table
                                                            style={{ width: '100%' }}
                                                          >
                                                            <thead
                                                              style={
                                                                {
                                                                  position: 'sticky',
                                                                  top: 0,
                                                                  backgroundColor: 'white',
                                                                  zIndex: 1,
                                                                }
                                                              }
                                                            >
                                                              <tr>
                                                                <th
                                                                  style={
                                                                    {
                                                                      border:
                                                                        '1px solid black',
                                                                      width: '50%',
                                                                    }
                                                                  }
                                                                >
                                                                  Privilege
                                                                </th>

                                                                <th
                                                                  style={
                                                                    {
                                                                      border:
                                                                        '1px solid black',
                                                                      width: '30%',
                                                                    }
                                                                  }
                                                                >
                                                                  Assign
                                                                </th>
                                                              </tr>
                                                            </thead>

                                                            <tbody>
                                                              {
                                                                renderPrivilegeItem(
                                                                  privilege,
                                                                )
                                                              }
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      </>
                                                    ),
                                                  )
                                                }
                                              </div>
                                            )
                                          }
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div> */}
                        </div>
                      </>
                    )
                  )
                }
              </Computed>

            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <div className="flex w-full items-center justify-end">

                      <Button
                        color="primary"
                        disabled={!canSubmit || !auth$.privileges.get()?.has('0.3.8.5')}
                        type="submit"
                        variant="contained"
                        onSubmit={
                          (e) => {
                            e.preventDefault()
                            formControl.handleSubmit()
                          }
                        }
                      >
                        Save
                      </Button>
                    </div>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
